import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const API_BASE_URL = "https://ims.holdthechild.net/api";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const getProposalsByRFQId = async (rfqId) => {
  try {
    const headers = getAuthHeaders();
    const url = `${API_BASE_URL}/vendor-submissions?rfqId=${rfqId}`;
    const res = await fetch(url, {
      method: "GET",
      headers: headers,
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`HTTP ${res.status}: ${errorText}`);
    }

    const data = await res.json();
    return data;
  } catch (err) {
    console.error(`Error fetching proposals for RFQ ${rfqId}:`, err);
    throw err;
  }
};

const createPO = async (data) => {
  try {
    const headers = getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/purchase-orders`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`HTTP ${res.status}: ${errorText}`);
    }

    const responseData = await res.json();
    return responseData;
  } catch (err) {
    console.error("Error creating PO:", err);
    throw err;
  }
};

export default function PurchaseOrderModal({ rfq, onClose, onSuccess }) {
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    vendorId: "",
    proposalId: "",
    items: "",
    totalAmount: "",
    deliveryDate: "",
    deliveryLocation: "",   // ✅ added
    poDate: new Date().toISOString().split("T")[0], // ✅ added default today
    rfqId: rfq?._id || "",
  });

  const getVendorId = (proposal) => {
    return (
      proposal.vendorId ||
      proposal.vendorCode ||
      proposal._id ||
      proposal.vendor?._id ||
      proposal.vendor?.id ||
      `vendor_${proposal._id}`
    );
  };

  const getItemsDescription = (proposal) => {
    if (proposal.items && Array.isArray(proposal.items) && proposal.items.length > 0) {
      return proposal.items
        .map(
          (item) =>
            `${item.description || "Item"} - Qty: ${item.quantity || 0} ${
              item.uom || ""
            } @ $${item.unitPrice || 0}`
        )
        .join("\n");
    }
    if (proposal.proposalSummary) return proposal.proposalSummary;
    if (proposal.description) return proposal.description;
    return `Proposal from ${proposal.vendorName || "Unknown Vendor"}`;
  };

  const getTotalAmount = (proposal) => {
    if (proposal.grandTotal) return proposal.grandTotal;
    if (proposal.totalCost) return proposal.totalCost;
    if (proposal.totalAmount) return proposal.totalAmount;
    if (proposal.amount) return proposal.amount;
    if (proposal.price) return proposal.price;
    if (proposal.subTotal) return proposal.subTotal;

    if (proposal.items && Array.isArray(proposal.items)) {
      const itemsTotal = proposal.items.reduce((sum, item) => {
        return (
          sum +
          (item.totalPriceWithVat ||
            item.totalPrice ||
            item.quantity * item.unitPrice ||
            0)
        );
      }, 0);
      if (itemsTotal > 0) return itemsTotal;
    }
    return 0;
  };

  useEffect(() => {
    if (!rfq?._id) {
      setError("No RFQ ID provided");
      setLoadingProposals(false);
      return;
    }

    const fetchProposals = async () => {
      setLoadingProposals(true);
      setError(null);

      try {
        const fetchedProposals = await getProposalsByRFQId(rfq._id);
        let proposalsArray = [];

        if (Array.isArray(fetchedProposals)) {
          proposalsArray = fetchedProposals;
        } else if (fetchedProposals && typeof fetchedProposals === "object") {
          if (Array.isArray(fetchedProposals.data)) {
            proposalsArray = fetchedProposals.data;
          } else if (Array.isArray(fetchedProposals.proposals)) {
            proposalsArray = fetchedProposals.proposals;
          } else if (Array.isArray(fetchedProposals.submissions)) {
            proposalsArray = fetchedProposals.submissions;
          } else {
            proposalsArray = [];
          }
        } else {
          proposalsArray = [];
        }

        setProposals(proposalsArray);
      } catch (error) {
        setError(error.message);
        setProposals([]);
      } finally {
        setLoadingProposals(false);
      }
    };

    fetchProposals();
  }, [rfq?._id]);

  const handleProposalSelect = (e) => {
    const proposalId = e.target.value;

    if (!proposalId) {
      setSelectedProposal(null);
      setFormData((prev) => ({
        ...prev,
        vendorId: "",
        proposalId: "",
        items: "",
        totalAmount: "",
      }));
      return;
    }

    const proposal = proposals.find((p) => p._id === proposalId);

    if (proposal) {
      const vendorId = getVendorId(proposal);
      const itemsDesc = getItemsDescription(proposal);
      const totalAmount = getTotalAmount(proposal);

      setSelectedProposal(proposal);
      setFormData((prev) => ({
        ...prev,
        vendorId: vendorId,
        proposalId: proposal._id,
        items: itemsDesc,
        totalAmount: totalAmount.toString(),
      }));
    }
  };

  const handleSubmit = async () => {
    if (!selectedProposal) {
      alert("Please select a valid proposal.");
      return;
    }
    if (!formData.deliveryDate) {
      alert("Please select a delivery date.");
      return;
    }
    if (!formData.deliveryLocation) {
      alert("Please enter a delivery location.");
      return;
    }

    try {
      setSubmitting(true);

      const poData = {
        ...formData,
        vendorName: selectedProposal.vendorName,
        contactPerson: selectedProposal.contactPerson,
        contactEmail: selectedProposal.contactEmail,
        paymentTerms: selectedProposal.paymentTerms,
        deliveryTimeline: selectedProposal.deliveryTimeline,
      };

      const responseData = await createPO(poData);

      onSuccess && onSuccess(responseData);
      onClose();
      alert("Purchase Order created successfully!");
    } catch (err) {
      alert(`Error creating Purchase Order: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white rounded-2xl shadow-lg w-[800px] max-h-[95vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500 z-10"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold text-blue-600 mb-6">
          Create Purchase Order
        </h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4">
            Error: {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select a Proposal:
            </label>
            <select
              value={formData.proposalId}
              onChange={handleProposalSelect}
              className="w-full border rounded-lg p-2"
              disabled={loadingProposals}
            >
              <option value="">
                {loadingProposals
                  ? "Loading..."
                  : proposals.length > 0
                  ? "Choose a proposal"
                  : "No proposals available"}
              </option>
              {proposals.map((proposal, index) => {
                const vendorName = proposal.vendorName || `Vendor ${index + 1}`;
                const amount = getTotalAmount(proposal);
                return (
                  <option
                    key={proposal._id || index}
                    value={proposal._id || index}
                  >
                    {`${vendorName} - $${amount.toLocaleString()}`}
                  </option>
                );
              })}
            </select>
          </div>

          {selectedProposal && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-green-800 mb-2">
                Selected Proposal Details:
              </h4>
              <div className="text-xs text-green-700 grid grid-cols-2 gap-2">
                <div>
                  <strong>Vendor:</strong> {selectedProposal.vendorName}
                </div>
                <div>
                  <strong>Contact:</strong> {selectedProposal.contactPerson}
                </div>
                <div>
                  <strong>Email:</strong> {selectedProposal.contactEmail}
                </div>
                <div>
                  <strong>Timeline:</strong> {selectedProposal.deliveryTimeline}
                </div>
                <div>
                  <strong>Payment:</strong> {selectedProposal.paymentTerms}
                </div>
                <div>
                  <strong>Valid Until:</strong>{" "}
                  {selectedProposal.validityPeriod
                    ? new Date(
                        selectedProposal.validityPeriod
                      ).toLocaleDateString()
                    : "N/A"}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vendor ID
              </label>
              <input
                type="text"
                value={formData.vendorId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, vendorId: e.target.value }))
                }
                className="w-full border rounded-lg p-2"
                placeholder="Auto-filled from proposal"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Total Amount ($)
              </label>
              <input
                type="number"
                value={formData.totalAmount}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    totalAmount: e.target.value,
                  }))
                }
                className="w-full border rounded-lg p-2"
                placeholder="Auto-filled from proposal"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Items & Description
            </label>
            <textarea
              value={formData.items}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, items: e.target.value }))
              }
              className="w-full border rounded-lg p-2 h-24"
              placeholder="Auto-filled from proposal items"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Delivery Date
            </label>
            <input
              type="date"
              value={formData.deliveryDate}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  deliveryDate: e.target.value,
                }))
              }
              min={new Date().toISOString().split("T")[0]}
              className="w-full border rounded-lg p-2"
              required
            />
          </div>

          {/* ✅ Added Delivery Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Delivery Location
            </label>
            <input
              type="text"
              value={formData.deliveryLocation}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  deliveryLocation: e.target.value,
                }))
              }
              className="w-full border rounded-lg p-2"
              placeholder="Enter delivery location"
              required
            />
          </div>

          {/* ✅ Added PO Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              PO Date
            </label>
            <input
              type="date"
              value={formData.poDate}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, poDate: e.target.value }))
              }
              className="w-full border rounded-lg p-2"
              required
            />
          </div>

          <button
            onClick={handleSubmit}
            className="bg-orange-500 text-white w-full py-3 rounded-lg hover:bg-orange-600 disabled:bg-gray-400 font-semibold"
            disabled={!selectedProposal || !formData.deliveryDate || submitting}
          >
            {submitting ? "Creating Purchase Order..." : "Create Purchase Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
